import React, {Fragment} from 'react';

import cs from 'classnames';
import {colors} from 'js-libs/sweetcommons/styles';
import styled from 'styled-components';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {
  ProcessProcedureTitleIcon,
  StepTitleAutomationIcon,
  StepTitleEndingIcon,
  StepTitleExclusiveGatewayIcon,
} from 'app/procedures/components/step-types-icons';
import {gt} from 'app/utils/i18n';

const StyledBaseNumberedStep = styled.div`
  font-size: 0.7em;
  position: relative;
  display: inline-block;
  height: 1.9em;
  width: 1.9em;
  font-weight: 700;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  color: ${colors.textDefault};
  fill: ${colors.textDefault};
  border-radius: 50%;
`;

type NumberedStepProps = {
  n: number | null;
  className: string;
};

/* moved-from step type */

const StyledMovedFromNumberedStep = styled(StyledBaseNumberedStep).attrs({
  className: 'd-flex align-items-center justify-content-center',
})`
  position: absolute;
  top: -1.2em;
  font-size: 0.5em;
  height: 1.9em;
  width: 1.9em;
  left: -0.4em;
  color: #fff;
  background: ${colors.linkBlue};
`;

function ProcedureMovedFromStepNumber({from}: {from: number}) {
  return (
    <LazyTooltip tooltip={gt('Step moved from position %s', from)}>
      <StyledMovedFromNumberedStep>{from}</StyledMovedFromNumberedStep>
    </LazyTooltip>
  );
}

/* content step type */

const StyledContentNumberedStep = styled(StyledBaseNumberedStep)`
  color: ${colors.lightBg};
  background: ${colors.textDefault};
  border-radius: 50%;
  border: 2px solid #fff;

  &.ins {
    color: ${colors.insGreenOpaque};
    background: ${colors.insGreenText};
  }

  &.del {
    color: ${colors.delRedOpaque};
    background: ${colors.delRedText};
  }
`;

const ProcedureContentStepNumber = ({n, className}: NumberedStepProps) => (
  <StyledContentNumberedStep className={className}>
    <div className="n">{n}</div>
  </StyledContentNumberedStep>
);

/* decision step type */

const StyledDecisionNumberedStep = styled(StyledBaseNumberedStep)`
  background: #fff;

  .n {
    position: absolute;
    top: 1.5em;
    left: 0;
    font-size: 0.7em;
    text-shadow: -2px 0 #fff, 0 2px #fff, 2px 0 #fff, 0 -2px #fff;
  }

  &.ins,
  &.del {
    border-radius: 50%;
  }

  &.ins {
    fill: ${colors.insGreenText};
    color: ${colors.insGreenText};
    background: ${colors.insGreen};
  }

  &.del {
    fill: ${colors.delRedText};
    color: ${colors.delRedText};
    background: ${colors.delRed};
  }
`;

const ProcedureDecisionStepNumber = ({n, className}: NumberedStepProps) => (
  <StyledDecisionNumberedStep className={className}>
    <StepTitleExclusiveGatewayIcon />
    <div className="n">{n}</div>
  </StyledDecisionNumberedStep>
);

/* ending step type */

const StyledEndingNumberedStep = styled(StyledBaseNumberedStep)`
  background: ${colors.textRed};
  color: ${colors.textRed};
  border: 2px solid #fff;

  .inner-circle {
    fill: white;
  }

  .n {
    text-shadow: -1px -1px #fff, 1px 1px #fff, 1px -1px #fff, -1px 1px #fff;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
  }

  &.ins .inner-circle {
    fill: ${colors.insGreenOpaque};
  }

  &.del .inner-circle {
    fill: ${colors.delRedOpaque};
  }
`;

const ProcedureEndingStepNumber = ({n, className}: NumberedStepProps) => (
  <StyledEndingNumberedStep className={className}>
    <StepTitleEndingIcon />
    <div className="n d-flex align-items-center justify-content-center">
      {n}
    </div>
  </StyledEndingNumberedStep>
);

/* process procedure type */

const StyledProcessProcedureNumberedStep = styled(StyledBaseNumberedStep)`
  background: #fff;

  .centered {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  .n {
    padding-top: 14%;
    font-size: 0.65em;
    text-shadow: -1px -1px #fff, 1px 1px #fff, 1px -1px #fff, -1px 1px #fff;
  }

  &.ins,
  &.del {
    border-radius: 50%;
  }

  &.ins {
    fill: ${colors.insGreenText};
    color: ${colors.insGreenText};
    background: ${colors.insGreen};
  }

  &.del {
    fill: ${colors.delRedText};
    color: ${colors.delRedText};
    background: ${colors.delRed};
  }
`;

const ProcessProcedureNumber = ({n, className}: NumberedStepProps) => (
  <StyledProcessProcedureNumberedStep className={className}>
    <div className="centered">
      <ProcessProcedureTitleIcon />
    </div>
    <div className="n centered">{n}</div>
  </StyledProcessProcedureNumberedStep>
);

const StyledAutomationProcedureNumberedStep = styled(
  StyledProcessProcedureNumberedStep
)`
  .n {
    font-size: 0.8em;
    top: 0.8em;
    left: 0;
  }
`;

const AutomationProcedureNumber = ({n, className}: NumberedStepProps) => (
  <StyledAutomationProcedureNumberedStep className={className}>
    <div className="centered">
      <StepTitleAutomationIcon />
    </div>
    <div className="n centered">{n}</div>
  </StyledAutomationProcedureNumberedStep>
);

/* generics */

const stepTypesIconsMap = {
  content: ProcedureContentStepNumber,
  decision: ProcedureDecisionStepNumber,
  ending: ProcedureEndingStepNumber,
  automation: AutomationProcedureNumber,
  processprocedure: ProcessProcedureNumber,
};

interface Props {
  n: number | null;
  type?: 'content' | 'decision' | 'ending' | 'processprocedure' | 'automation';
  changedState?: string;
  movedFrom?: number;
  className?: string;
}

export function ProcedureStepNumber({
  n,
  type,
  movedFrom,
  changedState,
  className,
}: Props) {
  const Component = stepTypesIconsMap[type || 'content'];
  return (
    <Fragment>
      {movedFrom != null ? (
        <ProcedureMovedFromStepNumber from={movedFrom} />
      ) : null}
      <Component
        className={cs(
          changedState,
          className,
          'd-flex align-items-center justify-content-center'
        )}
        n={n}
      />
    </Fragment>
  );
}
